import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faCheckCircle,
  faCircleNotch,
} from '@fortawesome/pro-solid-svg-icons';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { useShipments } from './queries';
import { apiPost } from '../../api';

function FeedbackForm({ isEmbedded }) {
  const [rateCrew, setRateCrew] = useState(null);
  const [rateOffice, setRateOffice] = useState(null);
  const [didUseAgent, setDidUseAgent] = useState(null);
  const [willRefer, setWillRefer] = useState(null);
  const [didRefer, setDidRefer] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const [errors, setErrors] = useState({
    crew: false,
    office: false,
  });
  const { register, handleSubmit } = useForm();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shipmentNumber, setShipmentNumber] = useState(null);
  const [email, setEmail] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [reviewLinkUrl, setReviewLinkUrl] = useState(null);
  const [feedbackText, setFeedbackText] = useState(null);
  const [commentCopied, setCommentCopied] = useState(false);
  // This form will try to get the data it needs (primarily
  // the shipment number) from the portal API, but to support
  // the embedded, outside of the portal, feedback form usecase
  // we also try to get the shipment number from the URL's
  // query string. If the query string looks like it has the data
  // we disable the react-query that normally provides that data.
  const queryParams = new URLSearchParams(window.location.search);

  let isUsingQueryParamData = false;
  if (
    !shipmentNumber &&
    queryParams.has('shipmentNumber') &&
    queryParams.has('email')
  ) {
    isUsingQueryParamData = true;
    setShipmentNumber(queryParams.get('shipmentNumber'));
    setEmail(queryParams.get('email'));
    setCustomerId(queryParams.get('customerID'));
  }

  const { isLoading, data: allShipments } = useShipments(
    !isUsingQueryParamData
  );

  useEffect(() => {
    if (allShipments && allShipments.shipment_number) {
      setShipmentNumber(allShipments.shipment_number);
    }
  }, [allShipments]);

  const onSubmit = async (data) => {
    setFeedbackText(data.feedbackText);
    setErrors({
      crew: false,
      office: false,
    });
    let foundError = false;
    const errorsFound = {
      crew: false,
      office: false,
    };
    if (rateCrew === null) {
      foundError = true;
      errorsFound.crew = true;
    }
    if (rateOffice === null) {
      foundError = true;
      errorsFound.office = true;
    }
    if (foundError) {
      setErrors({ ...errorsFound });
    } else {
      const payload = {
        ...data,
        rateCrew,
        rateOffice,
        didUseAgent,
        willRefer,
        didRefer,
        shipmentNumber,
        email,
      };
      console.log('payload? ', payload);

      setIsSubmitting(true);

      try {
        const response = await apiPost('feedback', payload);
        console.log(response);
        const reviewResponse = await apiPost('review-link-request', {
          shipmentNumber,
          customerId,
        });

        if (reviewResponse.ok) {
          const result = await reviewResponse.json();
          if (result[0]) {
            if (result[0].Parameters) {
              setReviewLinkUrl(result[0].Parameters.ReviewLink);
            }
          }
        }
        setIsSubmitted(true);
      } catch (e) {
        console.log(e);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const formStyles = {
    minHeight: 450,
  };

  if (isEmbedded) {
    formStyles.backgroundColor = '#fff';
  }

  return (
    <Row className="justify-content-center" style={formStyles}>
      <Col xl={9}>
        <div className="info-box mt-5">
          {isLoading && (
            <div className="text-center py-5">
              <FontAwesomeIcon
                icon={faCircleNotch}
                spin
                size="3x"
                style={{ color: '#953263' }}
              />
            </div>
          )}

          {isSubmitted && (
            <>
              <div className="info-header">
                <h3>Your survey is complete!</h3>
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="m-3">
                {reviewLinkUrl && (
                  <>
                    <p className="mb-1">
                      Thank you for sharing your feedback. We depend on
                      satisfied Gentle Giant customers to spread the word about
                      us and help grow our business. We would greatly appreciate
                      if you would share your feedback on Google as well, by
                      following this link:
                    </p>
                    <Button
                      href={reviewLinkUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="my-4"
                    >
                      Share with Google
                    </Button>
                    <p className="mb-1">
                      Copy my survey comment
                      <i
                        role="button"
                        tabIndex={0}
                        style={{ color: '#953263', fontSize: 'larger' }}
                        aria-label=" "
                        className="p-1 fa-solid fa-copy"
                        onKeyDown={null}
                        onClick={() => {
                          if (!navigator.clipboard) {
                            return;
                          }
                          navigator.clipboard
                            .writeText(feedbackText)
                            .then(() => {
                              setCommentCopied(true);
                            });
                        }}
                      />
                      {commentCopied && (
                        <span className="ms-2">
                          Copied
                          <i
                            role="button"
                            tabIndex={0}
                            style={{ color: '#456015', fontSize: 'larger' }}
                            aria-label="Copied"
                            className="p-1 fa-solid fa-check"
                          />
                        </span>
                      )}
                    </p>
                    <Form.Control
                      className="mb-3"
                      as="textarea"
                      rows={3}
                      value={feedbackText}
                      disabled
                    />
                  </>
                )}
              </div>
            </>
          )}

          {!isLoading && !isSubmitted && (
            <>
              <div className="info-header">
                <h3>Shipment #{shipmentNumber}</h3>
                <FontAwesomeIcon icon={faTruck} />
              </div>
              <div className="m-3">
                <p>
                  Gentle Giant is committed to every customer's complete
                  satisfaction and we strive to keep earning 80% of our business
                  from word-of-mouth and repeat customers.
                </p>
                <p>
                  To help us address any remaining needs and further enhance the
                  quality of our service, please take a moment to share your
                  feedback by completing this comment card.
                </p>
                <p>* indicates this field is required </p>
                <p className="bold-large">
                  How would you rate your experience with your moving crew? *
                </p>
                <Row className="mb-3">
                  <Col>
                    <Form.Group>
                      <Form.Check
                        type="radio"
                        label="Extraordinary"
                        checked={rateCrew === 5}
                        onChange={() => {
                          setRateCrew(5);
                        }}
                        isInvalid={errors.crew}
                      />
                      {errors.crew && (
                        <Form.Control.Feedback
                          type="invalid"
                          className="override-display"
                        >
                          This field is required.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Very Good"
                      checked={rateCrew === 4}
                      onChange={() => {
                        setRateCrew(4);
                      }}
                      isInvalid={errors.crew}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Good"
                      checked={rateCrew === 3}
                      onChange={() => {
                        setRateCrew(3);
                      }}
                      isInvalid={errors.crew}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Acceptable"
                      checked={rateCrew === 2}
                      onChange={() => {
                        setRateCrew(2);
                      }}
                      isInvalid={errors.crew}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Unsatisfactory"
                      checked={rateCrew === 1}
                      onChange={() => {
                        setRateCrew(1);
                      }}
                      isInvalid={errors.crew}
                    />
                  </Col>
                </Row>
                <p className="bold-large mb-3">
                  At Gentle Giant, we have always relied on our customers'
                  feedback to help improve what we do. Please share with our
                  president and founder, Larry O'Toole, any additional feedback
                  about your move that contributed to your experience.
                </p>
                <Form.Control
                  className="mb-3"
                  as="textarea"
                  rows={3}
                  {...register('feedbackText')}
                />
                <p className="bold-large">
                  How would you rate your experience working with the Gentle
                  Giant office team (receptionist, sales representatives, and
                  move coordinators)? *
                </p>
                <Row className="mb-3">
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Extraordinary"
                      checked={rateOffice === 5}
                      onChange={() => {
                        setRateOffice(5);
                      }}
                      isInvalid={errors.office}
                    />
                    {errors.office && (
                      <Form.Control.Feedback
                        type="invalid"
                        className="override-display"
                      >
                        This field is required.
                      </Form.Control.Feedback>
                    )}
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Very Good"
                      checked={rateOffice === 4}
                      onChange={() => {
                        setRateOffice(4);
                      }}
                      isInvalid={errors.office}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Good"
                      checked={rateOffice === 3}
                      onChange={() => {
                        setRateOffice(3);
                      }}
                      isInvalid={errors.office}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Acceptable"
                      checked={rateOffice === 2}
                      onChange={() => {
                        setRateOffice(2);
                      }}
                      isInvalid={errors.office}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="Unsatisfactory"
                      checked={rateOffice === 1}
                      onChange={() => {
                        setRateOffice(1);
                      }}
                      isInvalid={errors.office}
                    />
                  </Col>
                </Row>
                <p className="mb-3 bold-large">
                  Working with the real estate community provides great value to
                  Gentle Giant. Did you work with a real estate professional?
                </p>
                <Row className="mb-3">
                  <Col sm={2}>
                    <Form.Check
                      type="radio"
                      label="Yes"
                      checked={didUseAgent}
                      onChange={() => {
                        setDidUseAgent(true);
                      }}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="No"
                      checked={didUseAgent === false}
                      onChange={() => {
                        setDidUseAgent(false);
                      }}
                    />
                  </Col>
                </Row>
                {didUseAgent && (
                  <Row className="justify-content-center">
                    <Col sm={11}>
                      <p className="mb-3 bold-large">
                        Would you mind telling us who?
                      </p>

                      <Form.Group className="mb-3">
                        <Form.Label>Agent Name</Form.Label>
                        <Form.Control type="text" {...register('agentName')} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Agent Location</Form.Label>
                        <Form.Control
                          type="text"
                          {...register('agentLocation')}
                        />
                      </Form.Group>
                      <p className="mb-3 bold-large">
                        Did this real estate professional refer us?
                      </p>
                      <Row className="mb-3">
                        <Col sm={2}>
                          <Form.Check
                            type="radio"
                            label="Yes"
                            checked={didRefer}
                            onChange={() => {
                              setDidRefer(true);
                            }}
                          />
                        </Col>
                        <Col>
                          <Form.Check
                            type="radio"
                            label="No"
                            checked={didRefer === false}
                            onChange={() => {
                              setDidRefer(false);
                            }}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )}
                <p className="mb-3 bold-large">
                  Are you willing to be a reference and speak with someone who
                  is considering hiring Gentle Giant?
                </p>
                <Row className="mb-3">
                  <Col sm={2}>
                    <Form.Check
                      type="radio"
                      label="Yes"
                      checked={willRefer}
                      onChange={() => {
                        setWillRefer(true);
                      }}
                    />
                  </Col>
                  <Col>
                    <Form.Check
                      type="radio"
                      label="No"
                      checked={willRefer === false}
                      onChange={() => {
                        setWillRefer(false);
                      }}
                    />
                  </Col>
                </Row>
                <Button
                  variant="primary"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                  className="my-3"
                >
                  Submit
                  {isSubmitting && (
                    <>
                      {' '}
                      <FontAwesomeIcon
                        icon={faCircleNotch}
                        spin
                        size="1x"
                        style={{ color: '#fff' }}
                      />
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
}
FeedbackForm.defaultProps = {
  isEmbedded: false,
};

FeedbackForm.propTypes = {
  isEmbedded: PropTypes.bool,
};

export default FeedbackForm;
